import { Alert, Box, Button, Link, OutlinedInput, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { assetService, dataService } from "../../../../Core/Api/services";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import { Liquid } from "liquidjs";
import AceEditor, { IAnnotation } from "react-ace";

import Beautify from "ace-builds/src-noconflict/ext-beautify";
import { useHotkeys } from "react-hotkeys-hook";
import { Link as RouterLink, useParams } from "react-router";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import {
  AssetLiquidExportChilMode,
  DbDeliveryMetadataExportInfos,
} from "../../../../Core/Api/Types/db-delivery-metadata-export-infos";
import { routePaths } from "../../../../Router/router";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";

import "ace-builds/src-min-noconflict/mode-json";
import "ace-builds/src-min-noconflict/mode-liquid";
import "ace-builds/src-min-noconflict/mode-xml";
import "ace-builds/src-min-noconflict/theme-monokai";
import { DeliveryMetadataExportOrigin } from "../../../../Core/Api/Types/delivery-metadata-export-origin";
import { useTranslation } from "react-i18next";

function AdminSettingsLiquidExportEdit() {
  const { t } = useTranslation();
  const params = useParams();
  const exportId = parseInt(params.id || "-500");
  const enqueueSnackbar = useCustomSnackbar();

  const [exportObject, setExportObject] = useState<DbDeliveryMetadataExportInfos>();
  const [objectId, setObjectId] = useState("");

  const [json, setJson] = useState("");
  const [liquid, setLiquid] = useState("");
  const [output, setOutput] = useState("");

  const [annotations, setAnnotations] = useState<IAnnotation[]>([]);

  const engine = useMemo(() => new Liquid(), []);

  const jsonEditorRef = useRef<AceEditor | null>(null);

  const inputIdPlaceholder = useMemo(() => {
    if (!exportObject) return t("field:placeholder.putIdHere");

    switch (exportObject.origin) {
      case DeliveryMetadataExportOrigin.OrderProcessing:
        return t("field:placeholder.putOrderItemIdHere");
      case DeliveryMetadataExportOrigin.Asset:
        return t("field:placeholder.putAssetIdHere");
    }
  }, [exportObject]);

  const inputIdButtonText = useMemo(() => {
    if (!exportObject) return t("button:loadJson");

    switch (exportObject.origin) {
      case DeliveryMetadataExportOrigin.OrderProcessing:
        return t("button:loadOrderJson");
      case DeliveryMetadataExportOrigin.Asset:
        return t("button:loadAssetJson");
    }
  }, [exportObject]);

  const jsonTitle = useMemo(() => {
    if (!exportObject) return "Json";

    switch (exportObject.origin) {
      case DeliveryMetadataExportOrigin.OrderProcessing:
        return t("page:body.jsonTitle.orderJson");
      case DeliveryMetadataExportOrigin.Asset:
        return t("page:body.jsonTitle.assetJson");
    }
  }, [exportObject]);

  const jsonPlaceholder = useMemo(() => {
    if (!exportObject) return "Json";

    switch (exportObject.origin) {
      case DeliveryMetadataExportOrigin.OrderProcessing:
        return t("page:body.jsonPlaceholder.orderItemJson");
      case DeliveryMetadataExportOrigin.Asset:
        return t("page:body.jsonPlaceholder.assetObjectJson");
    }
  }, [exportObject]);

  const onLoadObjetId = () => {
    switch (exportObject!.origin) {
      case DeliveryMetadataExportOrigin.OrderProcessing:
        dataService.ExportOrderData(parseInt(objectId)).then((response) => {
          if (response.export) {
            setJson(JSON.stringify(response.export));
            handlePaste();
          }
        });
        break;
      case DeliveryMetadataExportOrigin.Asset:
        assetService.GetObjectById(parseInt(objectId)).then((response) => {
          if (response.graph) {
            setJson(JSON.stringify(response.graph));
            handlePaste();
          }
        });
        break;
    }
  };

  const onTemplateRun = () => {
    if (!json.length) {
      enqueueSnackbar(t("alert:warning.mustFillJsonField"), "warning");
      return;
    }

    try {
      const template = engine.parse(liquid);
      engine
        .render(template, JSON.parse(json))
        .then((value) => {
          dataService
            .RenderTemplate(exportObject!.id, parseInt(objectId), exportObject!.origin!, liquid)
            .then((response) => {
              if (response.success && response.message) setOutput(response.message);
              else enqueueSnackbar(t("alert:error.runningTemplate", { message: response.message }), "error");
            });
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      const message = (error as any).message as string;
      const splittedMessages = message.split(",");
      const col = parseInt(splittedMessages[2].replace("col:", ""));
      const line = parseInt(splittedMessages[1].replace("line:", ""));

      setAnnotations([
        {
          text: splittedMessages[0],
          type: "error",
          column: col - 1,
          row: line - 1,
        },
      ]);
    }
  };

  const formatJson = () => {
    jsonEditorRef.current!.editor.selectAll();
    Beautify.beautify(jsonEditorRef.current!.editor.session);
  };

  const handlePaste = () => {
    if (jsonEditorRef.current) {
      // Use setTimeout to prevent running formatJson() on an empty text area (event fired on paste, not pasted)
      setTimeout(() => {
        formatJson();
      }, 0);
    }
  };

  const handleValidateAndSaveTemplate = () => {
    if (exportId && exportId > 0)
      dataService
        .LiquidValidateAndSaveTemplate({
          id: exportId,
          template: liquid,
        })
        .then((response) => {
          if (response.success) enqueueSnackbar(t("alert:success.savedTemplate"), "success");
          else enqueueSnackbar(t("alert:error.savingTemplate", { message: response.message }), "error");
        });
  };

  useEffect(() => {
    if (jsonEditorRef.current) {
      jsonEditorRef.current.editor.on("paste", handlePaste);
    }
    return () => {
      if (jsonEditorRef.current) {
        jsonEditorRef.current.editor.off("paste", handlePaste);
      }
    };
  }, [jsonEditorRef.current]);

  useEffect(() => {
    if (exportId && exportId > 0)
      dataService.GetMetadataExport(exportId).then((response) => {
        if (response.export) {
          setExportObject(response.export);
          setLiquid(response.export.template || "");
        }
      });
  }, [exportId]);

  useHotkeys(
    "Control+s",
    () => {
      handleValidateAndSaveTemplate();
    },
    { preventDefault: true, enableOnContentEditable: true, enableOnFormTags: ["INPUT", "TEXTAREA"] },
    [liquid]
  );

  return (
    <Stack spacing={3}>
      <ListPageHeader>
        <ListPageHeaderLeft title={exportObject?.name ?? undefined}>
          <Stack direction="row" spacing={1}>
            <OutlinedInput
              placeholder={inputIdPlaceholder}
              value={objectId}
              onChange={(event) => setObjectId(event.target.value)}
            />
            <Button variant="contained" onClick={onLoadObjetId} disabled={!exportObject}>
              {inputIdButtonText}
            </Button>
          </Stack>
        </ListPageHeaderLeft>
        <ListPageHeaderRight>
          <Button
            variant="contained"
            startIcon={<PlayCircleOutlinedIcon />}
            onClick={onTemplateRun}
            disabled={!liquid.length}
          >
            {t("button:runCode")}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            onClick={handleValidateAndSaveTemplate}
            disabled={!liquid.length}
          >
            {t("button:saveTemplate")}
          </Button>
        </ListPageHeaderRight>
      </ListPageHeader>
      <Stack direction="row" gap={1} height={"100%"} width={"100%"} justifyContent="flex-start">
        <Stack gap={2} sx={{ flex: "1 0 0", minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {jsonTitle}
          </Typography>
          <AceEditor
            width="100%"
            ref={jsonEditorRef}
            name="json-input"
            theme="monokai"
            placeholder={jsonPlaceholder}
            mode="json"
            value={json}
            onChange={(value) => setJson(value)}
            commands={Beautify.commands as any}
          />
        </Stack>
        <Stack gap={2} sx={{ flex: "1 0 0", minWidth: 0 }}>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="subtitle2" fontWeight={600}>
              {t("page:body.liquidTemplate")}
            </Typography>
            <Tooltip
              title={
                <>
                  <Link
                    underline="hover"
                    variant="button"
                    fontWeight={500}
                    color="primary.text"
                    target="_blank"
                    component={RouterLink}
                    to={"https://shopify.github.io/liquid/"}
                  >
                    {t("button:liquidDocumentation")}
                  </Link>
                </>
              }
              arrow
              placement="right"
            >
              <HelpOutlineOutlinedIcon />
            </Tooltip>
          </Stack>
          <AceEditor
            width="100%"
            name="liquid-input"
            theme="monokai"
            placeholder={t("field:placeholder.liquidCode")}
            mode="liquid"
            value={liquid}
            onChange={(value) => setLiquid(value)}
            annotations={annotations}
            height={"100%"}
          />
        </Stack>
        <Stack spacing={2} sx={{ flex: "1 0 0", minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {exportObject?.liquidAssetExportFileType} {t("common:outputLowerCase")}
          </Typography>
          <AceEditor
            width="100%"
            name="xml-input"
            theme="monokai"
            placeholder={`${exportObject?.liquidAssetExportFileType} result`}
            mode="xml"
            value={output}
            onChange={(value) => setOutput(value)}
            readOnly
          />
        </Stack>
      </Stack>
      {exportObject && exportObject.liquidAssetExportChildMode === AssetLiquidExportChilMode.GroupWithParent && (
        <Alert severity="info">{t("alert:info.templateChildModeInstruction")}</Alert>
      )}
    </Stack>
  );
}

export default AdminSettingsLiquidExportEdit;
